/**
 * Indicadores gráficos leves do dashboard (Entrega 2, item 2.3) · redesign R9.
 *
 * Redesign R9 (apresentação apenas): o traçado passa a ser desenhado pelo
 * `recharts` (lib genérica de gráfico, aprovada para esta frente — não é o
 * pacote do template, é uma dependência normal do app, sem CDN) em vez de
 * `<div>`/CSS artesanal, para o visual bater com o padrão de card de gráfico
 * do redesign. Os dois invariantes de acessibilidade da versão anterior
 * CONTINUAM:
 *
 *  1. **O gráfico NUNCA é o único portador de informação** (WCAG 1.1.1/1.4.1):
 *     o contêiner do SVG é `aria-hidden`, e o grupo inteiro carrega
 *     `role="img"` + `aria-label` com "rótulo: valor" de cada barra por
 *     extenso — o SVG é puro reforço visual, o nome acessível já diz tudo.
 *  2. **Dimensões fixas** (altura calculada pelo número de barras) para CLS=0.
 *
 * O componente só DESENHA números que já vêm prontos dos seletores do
 * dashboard (`buildDashboardKpis`/`buildCommandCenterStats`); não recalcula,
 * não interpola e não inventa série histórica — não existe hoje uma fonte de
 * dados por período (ex. faturamento mês a mês) no modelo do dashboard, então
 * nenhum gráfico de tendência é criado aqui até essa fonte existir.
 */
import { createElement as h, type ReactElement } from "react";
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, XAxis, YAxis } from "recharts";

export interface MiniBar {
  /** Rótulo curto da barra (ex.: "Dentro do SLA"). */
  label: string;
  /** Valor numérico que define o comprimento da barra. */
  value: number;
  /** Texto já formatado exibido ao final da barra (ex.: "50%", "R$ 2.500"). */
  display: string;
  /** `true` destaca a barra com o accent vermelho da marca (atenção). */
  emphasis?: boolean;
}

export interface MiniBarChartProps {
  /** Nome acessível do conjunto — vira o `aria-label` do grupo. */
  title: string;
  bars: MiniBar[];
  "data-chart"?: string;
}

const ROW_HEIGHT_PX = 40;
const MIN_HEIGHT_PX = 88;

/**
 * Barras horizontais com rótulo (eixo) + valor textual ao final da barra. A
 * largura relativa é só reforço visual da hierarquia; o número textual
 * (`aria-label` do grupo) é a fonte de verdade para leitor de tela.
 */
export function MiniBarChart({ title, bars, ...rest }: MiniBarChartProps): ReactElement {
  const summary = bars.map((bar) => `${bar.label}: ${bar.display}`).join("; ");
  const height = Math.max(MIN_HEIGHT_PX, bars.length * ROW_HEIGHT_PX);

  return h(
    "div",
    {
      className: "flex flex-col gap-2",
      role: "img",
      "aria-label": `${title}. ${summary}`,
      "data-chart": rest["data-chart"]
    },
    h("div", { style: { width: "100%", height }, "aria-hidden": true }, h(ResponsiveContainer, { width: "100%", height: "100%", children: chart(bars) }))
  );
}

/** Monta a árvore do `BarChart` separada do wrapper acessível (clareza de parênteses). */
function chart(bars: MiniBar[]): ReactElement {
  return h(
    BarChart,
    { data: bars, accessibilityLayer: false, layout: "vertical", margin: { top: 4, right: 56, bottom: 4, left: 4 } },
    h(XAxis, { type: "number", hide: true }),
    h(YAxis, {
      type: "category",
      dataKey: "label",
      width: 140,
      tickLine: false,
      axisLine: false,
      tick: { fill: "var(--s2-muted)", fontSize: 12 }
    }),
    h(
      Bar,
      { dataKey: "value", radius: [0, 8, 8, 0], barSize: 14, isAnimationActive: false },
      ...bars.map((bar, index) =>
        h(Cell, {
          key: `cell-${bar.label}-${index}`,
          fill: bar.emphasis ? "var(--s2-brand)" : "var(--s2-border-strong)"
        })
      ),
      h(LabelList, {
        dataKey: "display",
        position: "right",
        style: { fill: "var(--s2-ink)", fontSize: 13, fontWeight: 600 }
      })
    )
  );
}

export interface MiniColumn {
  label: string;
  value: number;
  /** Texto já formatado do valor (usado no resumo acessível e no tooltip nativo). */
  display: string;
}

export interface MiniColumnChartProps {
  title: string;
  columns: MiniColumn[];
  "data-chart"?: string;
}

/**
 * Colunas verticais para série por período (C5). Mesmos invariantes do
 * `MiniBarChart`: o SVG é `aria-hidden` e o `role="img"` + `aria-label` lista
 * "dia: valor" por extenso (o gráfico nunca é o único portador do dado) e a
 * altura é fixa (CLS=0). Só desenha o que a série derivada já calculou.
 */
export function MiniColumnChart({ title, columns, ...rest }: MiniColumnChartProps): ReactElement {
  const summary = columns.map((column) => `${column.label}: ${column.display}`).join("; ");
  return h(
    "div",
    { className: "flex flex-col gap-2", role: "img", "aria-label": `${title}. ${summary}`, "data-chart": rest["data-chart"] },
    h(
      "div",
      { style: { width: "100%", height: 200 }, "aria-hidden": true },
      h(ResponsiveContainer, {
        width: "100%",
        height: "100%",
        children: h(
          BarChart,
          { data: columns, accessibilityLayer: false, margin: { top: 8, right: 8, bottom: 4, left: 8 } },
          h(XAxis, { dataKey: "label", tickLine: false, axisLine: false, interval: columns.length > 12 ? 2 : 0, tick: { fill: "var(--s2-muted)", fontSize: 12 } }),
          h(YAxis, { hide: true }),
          h(Bar, { dataKey: "value", radius: [6, 6, 0, 0], fill: "var(--s2-brand)", isAnimationActive: false })
        )
      })
    )
  );
}
